import { useEffect, useMemo, useRef, useState } from 'react';
import { useCreate, useNotify, useRefresh } from 'react-admin';
import { usePlaidLink } from 'react-plaid-link';
import { plaidResources } from '../utils/plaidConfig';

type WorkflowType = 'bank' | 'payroll';
type Status = 'idle' | 'launching' | 'linking' | 'submitting' | 'done' | 'error';

type Args = {
  type: WorkflowType;
  trn_id?: string | null;
  onDone?: () => void;
  onPlaidSuccess?: () => void;
  onBeforeOpen?: () => void;
  onExit?: (completed: boolean) => void;
  linkTokenData?: Record<string, any>;
};

export function usePlaidIncomeWorkflow({
  type,
  trn_id,
  onDone,
  onPlaidSuccess,
  onBeforeOpen,
  onExit,
  linkTokenData,
}: Args) {
  const cfg = useMemo(() => plaidResources[type], [type]);
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();

  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const statusRef = useRef<Status>('idle');
  const deferDoneUntilExitRef = useRef<boolean>(false);
  const hasExitedRef = useRef<boolean>(false);
  const plaidSuccessSeenRef = useRef<boolean>(false);
  const pendingExitUnlockTimerRef = useRef<number | null>(null);
  const failureExitEmittedRef = useRef<boolean>(false);

  const updateStatus = (next: Status) => {
    statusRef.current = next;
    setStatus(next);
  };

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const restoreScroll = () => {
    try {
      if (typeof document === 'undefined') return;
      const targets = [document.body, document.documentElement];
      const props = [
        'overflow',
        'overflowX',
        'overflowY',
        'position',
        'height',
        'width',
        'top',
        'left',
        'right',
        'paddingRight',
      ];
      const classes = ['plaid-link-open', 'plaid-link--is-open', 'body--plaid-link-open'];

      targets.forEach((node) => {
        if (!node) return;
        props.forEach((prop) => node.style.removeProperty(prop));
        classes.forEach((cls) => node.classList?.remove(cls));
      });
    } catch {}
  };

  const restoreScrollSoon = () => {
    restoreScroll();
    if (typeof window !== 'undefined') {
      window.setTimeout(restoreScroll, 100);
    }
  };

  const finishWithoutPost = () => {
    notify(cfg.notifySuccess, { type: 'success' });
    refresh();
    setStatus('done');
    restoreScrollSoon();
    onDone?.();
  };

  const emitFailureExit = () => {
    if (failureExitEmittedRef.current) return;
    failureExitEmittedRef.current = true;
    onExit?.(false);
  };

  const { open, ready } = usePlaidLink({
    token: linkToken ?? null,
    onSuccess: (publicToken, metadata) => {
      const linkSessionId =
        typeof metadata?.link_session_id === 'string'
          ? metadata.link_session_id.trim()
          : '';
      if (type === 'payroll') {
        onPlaidSuccess?.();
      }
      if (cfg.linkSuccessResource) {
        plaidSuccessSeenRef.current = true;
        updateStatus('submitting');
        const linkSuccessPayload: Record<string, any> = {
          trn_id: trn_id ?? "",
          public_token: publicToken,
        };
        if (linkSessionId) {
          linkSuccessPayload.link_session_id = linkSessionId;
        }
        if (type === "payroll" && linkTokenData?.source_id) {
          linkSuccessPayload.source_id = linkTokenData.source_id;
        }
        create(
          cfg.linkSuccessResource,
          { data: linkSuccessPayload },
          {
            onSuccess: () => {
              notify(cfg.notifySuccess, { type: 'success' });
              refresh();
              updateStatus('done');
              restoreScrollSoon();
              if (type === 'payroll') {
                if (hasExitedRef.current) {
                  onDone?.();
                } else {
                  deferDoneUntilExitRef.current = true;
                }
              } else {
                onDone?.();
              }
            },
            onError: () => {
              deferDoneUntilExitRef.current = false;
              setError('submit_failed');
              updateStatus('error');
              restoreScrollSoon();
              emitFailureExit();
            },
          }
        );
        return;
      }

      finishWithoutPost();
    },
    onExit: () => {
      hasExitedRef.current = true;
      restoreScrollSoon();
      const completed =
        statusRef.current === 'done' || statusRef.current === 'submitting';
      if (statusRef.current === 'done' && deferDoneUntilExitRef.current) {
        deferDoneUntilExitRef.current = false;
        onDone?.();
      }
      if (statusRef.current !== 'done' && statusRef.current !== 'submitting') {
        if (pendingExitUnlockTimerRef.current) {
          window.clearTimeout(pendingExitUnlockTimerRef.current);
        }
        pendingExitUnlockTimerRef.current = window.setTimeout(() => {
          pendingExitUnlockTimerRef.current = null;
          if (plaidSuccessSeenRef.current) {
            return;
          }
          updateStatus('idle');
          emitFailureExit();
        }, 1000);
        return;
      }
      onExit?.(completed);
    },
  });

  useEffect(() => {
    return () => {
      if (pendingExitUnlockTimerRef.current) {
        window.clearTimeout(pendingExitUnlockTimerRef.current);
      }
      restoreScrollSoon();
    };
  }, []);

  useEffect(() => {
    if (ready && linkToken && status === 'linking') {
      onBeforeOpen?.();
      open();
    }
  }, [ready, linkToken, status, open, onBeforeOpen]);

  const start = () => {
    if (!trn_id) return;
    if (status === 'launching' || status === 'linking' || status === 'submitting') return;
    deferDoneUntilExitRef.current = false;
    hasExitedRef.current = false;
    plaidSuccessSeenRef.current = false;
    failureExitEmittedRef.current = false;
    if (pendingExitUnlockTimerRef.current) {
      window.clearTimeout(pendingExitUnlockTimerRef.current);
      pendingExitUnlockTimerRef.current = null;
    }
    setError(undefined);
    updateStatus('launching');

    create(
      cfg.linkTokenResource,
      { data: { trn_id, ...(linkTokenData || {}) } },
      {
        onSuccess: (data: any) => {
          notify(cfg.notifyLaunch, { type: 'success' });
          setLinkToken(data.linkToken);
          updateStatus('linking');
        },
        onError: () => {
          notify(cfg.notifyError, { type: 'error' });
          setError('token_failed');
          updateStatus('error');
        },
      }
    );
  };

  const cancel = () => {
    deferDoneUntilExitRef.current = false;
    hasExitedRef.current = false;
    plaidSuccessSeenRef.current = false;
    failureExitEmittedRef.current = false;
    if (pendingExitUnlockTimerRef.current) {
      window.clearTimeout(pendingExitUnlockTimerRef.current);
      pendingExitUnlockTimerRef.current = null;
    }
    updateStatus('idle');
    setError(undefined);
    setLinkToken(null);
    restoreScrollSoon();
  };

  return {
    state: { status, error },
    start,
    cancel,
    disabled: status === 'launching' || status === 'linking' || status === 'submitting',
  } as const;
}
